import type { FC } from 'react';
import React, { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import type { LinkUrl, WSLineOutcomesSB } from '@mwl/core-lib';
import { debounceFn, getCompetitors, loadCouponPreviewV2Request, removeSelectedBetV2, useWebsocketLineOutcomes, useWebsocketLineStatusV2 } from '@mwl/core-lib';
import CrossIcon from '@public/assets/common/icons/close.svg';
import LockIcon from '@public/assets/common/icons/lock.svg';
import { ButtonIcon } from '@/components/ButtonIcon/ButtonIcon';
import { SportIcon } from '@/components/SportIcon/SportIcon';
import { base, routes } from '@/constants';
import { useTypedDispatch } from '@/hooks';
import { CompetitorsWrapper } from '../CompetitorsWrapper/CompetitorsWrapper';
import { CouponActingOdd } from '../CouponActingOdd/CouponActingOdd';
import type { CouponBetProps } from './CouponBet.types';
import { findMarketOutcome } from './CouponBet.utils';
import styles from './CouponBet.module.scss';
const requestDebounceTime = 300;
const BaseCouponBet: FC<CouponBetProps> = ({
  withRefuse,
  sport,
  championship,
  market,
  blocked,
  outcome,
  line
}) => {
  const {
    t
  } = useTranslation('sport');
  const dispatch = useTypedDispatch();
  const handleRefuse = () => {
    if (!outcome) {
      return;
    }
    dispatch(removeSelectedBetV2({
      outcomeId: outcome.id
    }));
  };
  const {
    HOME,
    AWAY
  } = getCompetitors(line?.competitors ?? []);
  const debounceCouponPreview = useMemo(() => debounceFn(() => {
    dispatch(loadCouponPreviewV2Request());
  }, requestDebounceTime), [dispatch]);
  const onStatusUpdate = useCallback(() => {
    debounceCouponPreview();
  }, [debounceCouponPreview]);
  const onOutcomeUpdate = useCallback(async (data: WSLineOutcomesSB) => {
    if (!outcome?.id) {
      return;
    }
    if (data.markets && data.lineId) {
      const foundOutcome = findMarketOutcome(data.markets, outcome.id);
      if (foundOutcome) {
        debounceCouponPreview();
      }
    }
  }, [outcome?.id, debounceCouponPreview]);
  const lineHref = useMemo<LinkUrl | undefined>(() => {
    if (!line?.id) {
      return undefined;
    }
    return {
      pathname: routes.sport.line,
      query: {
        lineId: line?.id
      }
    };
  }, [line?.id]);
  useWebsocketLineOutcomes({
    lineId: line?.id,
    onUpdate: onOutcomeUpdate,
    projectName: base.NAME_PROJECT
  });
  useWebsocketLineStatusV2({
    lineId: line?.id,
    onUpdate: onStatusUpdate,
    projectName: base.NAME_PROJECT
  });
  if (!outcome) {
    return null;
  }
  return <div className={cn(styles.root, {
    [styles.withRefuse]: withRefuse
  })} data-sentry-component="BaseCouponBet" data-sentry-source-file="CouponBet.tsx">
      {blocked && withRefuse && <div className={styles.blockedStub}>
          <div className={styles.blockedContent}>
            <LockIcon className={styles.blockedIcon} />
            <p className={styles.blockedTitle}>{t('coupon.bet.blocked', 'Blocked')}</p>
          </div>
        </div>}
      <div className={styles.matchInfo}>
        <SportIcon url={sport?.icon} size={20} className={styles.sportIcon} data-sentry-element="SportIcon" data-sentry-source-file="CouponBet.tsx" />
        <span className={styles.championshipTitle}>{championship?.title}</span>
      </div>
      <CompetitorsWrapper lineHref={lineHref} className={cn(styles.competitors, {
      [styles.link]: !!line?.id
    })} data-sentry-element="CompetitorsWrapper" data-sentry-source-file="CouponBet.tsx">
        {HOME?.title && <span className={styles.team}>{HOME?.title}</span>}
        {HOME?.title && AWAY?.title && <span>&nbsp;- </span>}
        {AWAY?.title && <span className={styles.team}>{AWAY?.title}</span>}
      </CompetitorsWrapper>

      <div className={styles.outcomeRow}>
        <div className={styles.outcomeData}>
          {market?.title && `${market?.title} =`} {outcome.title}
        </div>
        <CouponActingOdd odd={outcome.odds} className={styles.odd} data-sentry-element="CouponActingOdd" data-sentry-source-file="CouponBet.tsx" />
      </div>
      {withRefuse && <ButtonIcon className={styles.removeButton} as="button" onClick={handleRefuse} aria-label="coupon bet refuse">
          <CrossIcon />
        </ButtonIcon>}
    </div>;
};
export const CouponBet = memo(BaseCouponBet);
CouponBet.displayName = 'CouponBet';